import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import DestinationCard from './DestinationCard';
import DestinationCardTwo from './DestinationCardTwo';
import TourCard from '../Tour/TourCard';
import TourCardTwo from '../Tour/TourCardTwo';
import { fetchDestinations, fetchToursByDestination } from '../../services/api';

function DestinationInner() {
    const location = useLocation();
    const [activeTab, setActiveTab] = useState('tab-grid');
    const [currentPage, setCurrentPage] = useState(1);
    const [destinations, setDestinations] = useState([]);
    const [tours, setTours] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedDestination, setSelectedDestination] = useState(null);
    const [pagination, setPagination] = useState({
        meta: {},
        links: {}
    });

    // Handle URL query parameters for destination selection
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const destinationId = searchParams.get('destination');

        if (destinationId && !selectedDestination) {
            // Fetch the destination details to set it as selected
            const loadDestinationById = async () => {
                try {
                    const response = await fetchDestinations(1, '', '');
                    const destination = response.destinations.find(d => d.id === parseInt(destinationId));
                    if (destination) {
                        setSelectedDestination(destination);
                    }
                } catch (err) {
                    console.error('Error loading destination from URL:', err);
                }
            };
            loadDestinationById();
        } else if (!destinationId && selectedDestination) {
            // Clear selection if no destination in URL
            setSelectedDestination(null);
        }
    }, [location.search]);

    // Load destinations or tours based on selection
    useEffect(() => {
        if (selectedDestination) {
            loadTours(selectedDestination.id, currentPage);
        } else {
            loadDestinations(currentPage, searchQuery);
        }
    }, [currentPage, searchQuery, selectedDestination]);

    const loadDestinations = async (page, search = '') => {
        try {
            setLoading(true);
            setError(null);
            const response = await fetchDestinations(page, search, '');
            setDestinations(response.destinations);
            setPagination(response.pagination);
        } catch (err) {
            setError(err.message || 'Failed to load destinations. Please try again later.');
            console.error('Error loading destinations:', err);
        } finally {
            setLoading(false);
        }
    };

    const loadTours = async (destinationId, page) => {
        try {
            setLoading(true);
            setError(null);
            const response = await fetchToursByDestination(destinationId, page);
            setTours(response.tours);
            setPagination(response.pagination);
        } catch (err) {
            setError(err.message || 'Failed to load tours. Please try again later.');
            console.error('Error loading tours:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);
        const searchValue = formData.get('search') || '';
        setSearchQuery(searchValue);
        setCurrentPage(1); // Reset to first page on new search
    };

    const handleDestinationClick = (destination) => {
        setSelectedDestination(destination);
        setCurrentPage(1); // Reset to first page when selecting destination
    };

    const handleBackToDestinations = () => {
        setSelectedDestination(null);
        setCurrentPage(1);
        setSearchQuery('');
    };

    const handlePageChange = (page) => {
        if (page >= 1 && page <= (pagination.meta.last_page || 1)) {
            setCurrentPage(page);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    // Decode HTML entities helper function
    const decodeHtml = (html) => {
        const txt = document.createElement('textarea');
        txt.innerHTML = html;
        return txt.value;
    };

    // Render Destinations View (when no destination selected)
    const renderDestinationsView = () => (
        <>
            {/* Sort Bar */}
            <div className="th-sort-bar">
                <div className="row justify-content-between align-items-center">
                    <div className="col-md-4">
                        <div className="search-form-area">
                            <form className="search-form" onSubmit={handleSearch}>
                                <input
                                    type="text"
                                    name="search"
                                    placeholder="Search destinations"
                                    defaultValue={searchQuery}
                                />
                                <button type="submit">
                                    <i className="fa-light fa-magnifying-glass" />
                                </button>
                            </form>
                        </div>
                    </div>
                    <div className="col-md-auto">
                        <div className="sorting-filter-wrap">
                            <div className="nav" role="tablist">
                                <Link
                                    to="#"
                                    id="tab-destination-grid"
                                    data-bs-toggle="tab"
                                    data-bs-target="#tab-grid"
                                    role="tab"
                                    aria-controls="tab-grid"
                                    aria-selected="true"
                                    className={`${activeTab === 'tab-grid' ? 'active' : ''}`}
                                    type="button"
                                    onClick={() => setActiveTab('tab-grid')}
                                >
                                    <i className="fa-light fa-grid-2" />
                                </Link>
                                <Link
                                    to="#"
                                    id="tab-destination-list"
                                    data-bs-toggle="tab"
                                    data-bs-target="#tab-list"
                                    role="tab"
                                    aria-controls="tab-list"
                                    aria-selected="false"
                                    className={`${activeTab === 'tab-list' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('tab-list')}
                                >
                                    <i className="fa-solid fa-list" />
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Destinations Content */}
            <div className="row">
                <div className="col-12">
                    {loading && (
                        <div className="text-center py-5">
                            <p>Loading destinations...</p>
                        </div>
                    )}

                    {error && (
                        <div className="alert alert-danger" role="alert">
                            <strong>Error:</strong> {error}
                        </div>
                    )}

                    {!loading && !error && (
                        <div className="tab-content" id="nav-tabContent">
                            <div className={`tab-pane fade ${activeTab === 'tab-grid' ? 'show active' : ''}`} id="tab-grid" role="tabpanel">
                                {destinations.length === 0 ? (
                                    <div className="text-center py-5">
                                        <p>No destinations found.</p>
                                    </div>
                                ) : (
                                    <div className="row gy-30">
                                        {destinations.map((data) => (
                                            <div key={data.id} className="col-xxl-4 col-xl-6">
                                                <DestinationCard
                                                    destination={data}
                                                    onClick={handleDestinationClick}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className={`tab-pane fade ${activeTab === 'tab-list' ? 'show active' : ''}`} id="tab-list" role="tabpanel">
                                {destinations.length === 0 ? (
                                    <div className="text-center py-5">
                                        <p>No destinations found.</p>
                                    </div>
                                ) : (
                                    <div className="row gy-30">
                                        {destinations.map((data) => (
                                            <div key={data.id} className="col-12">
                                                <DestinationCardTwo
                                                    destination={data}
                                                    onClick={handleDestinationClick}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {!loading && !error && pagination.meta.last_page > 1 && (
                        <div className="th-pagination text-center mt-60 mb-0">
                            <ul>
                                {pagination.meta.links && pagination.meta.links.map((link, index) => {
                                    if (link.url === null || link.page === null) {
                                        return null;
                                    }

                                    const pageNumber = link.page;
                                    const isActive = link.active;
                                    const isPrevious = link.label.includes('Previous');
                                    const isNext = link.label.includes('Next');

                                    let displayLabel = decodeHtml(link.label);
                                    if (isNext) {
                                        displayLabel = displayLabel.replace('»', '').trim();
                                    } else if (isPrevious) {
                                        displayLabel = displayLabel.replace('«', '').trim();
                                    }

                                    return (
                                        <li key={index}>
                                            <Link
                                                className={`${isActive ? 'active' : ''} ${isNext ? 'next-page' : ''}`}
                                                to="#"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    if (pageNumber) {
                                                        handlePageChange(pageNumber);
                                                    }
                                                }}
                                            >
                                                {isNext && (
                                                    <>
                                                        {displayLabel} <img src="/assets/img/icon/arrow-right4.svg" alt="" />
                                                    </>
                                                )}
                                                {!isNext && displayLabel}
                                            </Link>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    )}
                </div>
            </div>
        </>
    );

    // Render Tours View (when a destination is selected)
    const renderToursView = () => (
        <>
            {/* Breadcrumb and Back Navigation */}
            <div className="row mb-4">
                <div className="col-12">
                    <nav aria-label="breadcrumb" className="destination-breadcrumb">
                        <ol className="breadcrumb mb-2" style={{ background: 'transparent', padding: 0 }}>
                            <li className="breadcrumb-item">
                                <Link
                                    to="#"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleBackToDestinations();
                                    }}
                                    style={{ color: '#007bff' }}
                                >
                                    Destinations
                                </Link>
                            </li>
                            <li className="breadcrumb-item active" aria-current="page">
                                {selectedDestination?.title}
                            </li>
                        </ol>
                    </nav>
                    <Link
                        to="#"
                        onClick={(e) => {
                            e.preventDefault();
                            handleBackToDestinations();
                        }}
                        className="th-btn style3"
                        style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                    >
                        <i className="fa-solid fa-arrow-left" /> Back to Destinations
                    </Link>
                </div>
            </div>

            {/* Sort Bar */}
            <div className="th-sort-bar">
                <div className="row justify-content-between align-items-center">
                    <div className="col-md-4">
                        <h4>Tours in {selectedDestination?.title}</h4>
                    </div>
                    <div className="col-md-auto">
                        <div className="sorting-filter-wrap">
                            <div className="nav" role="tablist">
                                <Link
                                    to="#"
                                    id="tab-tour-grid"
                                    data-bs-toggle="tab"
                                    data-bs-target="#tab-grid"
                                    role="tab"
                                    aria-controls="tab-grid"
                                    aria-selected="true"
                                    className={`${activeTab === 'tab-grid' ? 'active' : ''}`}
                                    type="button"
                                    onClick={() => setActiveTab('tab-grid')}
                                >
                                    <i className="fa-light fa-grid-2" />
                                </Link>
                                <Link
                                    to="#"
                                    id="tab-tour-list"
                                    data-bs-toggle="tab"
                                    data-bs-target="#tab-list"
                                    role="tab"
                                    aria-controls="tab-list"
                                    aria-selected="false"
                                    className={`${activeTab === 'tab-list' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('tab-list')}
                                >
                                    <i className="fa-solid fa-list" />
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tours Content */}
            <div className="row">
                <div className="col-12">
                    {loading && (
                        <div className="text-center py-5">
                            <p>Loading tours...</p>
                        </div>
                    )}

                    {error && (
                        <div className="alert alert-danger" role="alert">
                            <strong>Error:</strong> {error}
                        </div>
                    )}

                    {!loading && !error && (
                        <div className="tab-content" id="nav-tabContent">
                            <div className={`tab-pane fade ${activeTab === 'tab-grid' ? 'show active' : ''}`} id="tab-grid" role="tabpanel">
                                {tours.length === 0 ? (
                                    <div className="text-center py-5">
                                        <p>No tours found for {selectedDestination?.title}.</p>
                                    </div>
                                ) : (
                                    <div className="row gy-30">
                                        {tours.map((data) => (
                                            <div key={data.id} className="col-xxl-4 col-xl-6">
                                                <TourCard
                                                    tour={data}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className={`tab-pane fade ${activeTab === 'tab-list' ? 'show active' : ''}`} id="tab-list" role="tabpanel">
                                {tours.length === 0 ? (
                                    <div className="text-center py-5">
                                        <p>No tours found for {selectedDestination?.title}.</p>
                                    </div>
                                ) : (
                                    <div className="row gy-30">
                                        {tours.map((data) => (
                                            <div key={data.id} className="col-12">
                                                <TourCardTwo
                                                    tour={data}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {!loading && !error && pagination.meta.last_page > 1 && (
                        <div className="th-pagination text-center mt-60 mb-0">
                            <ul>
                                {pagination.meta.links && pagination.meta.links.map((link, index) => {
                                    if (link.url === null || link.page === null) {
                                        return null;
                                    }

                                    const pageNumber = link.page;
                                    const isActive = link.active;
                                    const isPrevious = link.label.includes('Previous');
                                    const isNext = link.label.includes('Next');

                                    let displayLabel = decodeHtml(link.label);
                                    if (isNext) {
                                        displayLabel = displayLabel.replace('»', '').trim();
                                    } else if (isPrevious) {
                                        displayLabel = displayLabel.replace('«', '').trim();
                                    }

                                    return (
                                        <li key={index}>
                                            <Link
                                                className={`${isActive ? 'active' : ''} ${isNext ? 'next-page' : ''}`}
                                                to="#"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    if (pageNumber) {
                                                        handlePageChange(pageNumber);
                                                    }
                                                }}
                                            >
                                                {isNext && (
                                                    <>
                                                        {displayLabel} <img src="/assets/img/icon/arrow-right4.svg" alt="" />
                                                    </>
                                                )}
                                                {!isNext && displayLabel}
                                            </Link>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    )}
                </div>
            </div>
        </>
    );

    return (
        <section className="space">
            <div className="container">
                {selectedDestination ? renderToursView() : renderDestinationsView()}
            </div>
        </section>
    )
}

export default DestinationInner
